<template>
  <div class="benefit-accordion" :class="{ 'solo': isSolo }">

    <button
      v-if="!isSolo"
      class="accordion-header"
      :class="{ 'open': isOpen }"
      @click="$emit('toggle', group.id)"
      :aria-expanded="isOpen"
      :aria-controls="`accordion-body-${group.id}`"
      type="button"
    >
      <div class="accordion-header__left">
        <span class="accordion-header__title" :title="group.name">{{ group.name }}</span>
      </div>

      <div class="accordion-header__right">
        <span v-if="isLocked" class="accordion-header__locked-badge">Locked</span>
        <span class="accordion-header__count">{{ group.benefits.length }} {{ group.benefits.length === 1 ? 'benefit' : 'benefits' }}</span>
        <svg
          class="accordion-header__chevron"
          :class="{ 'open': isOpen }"
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          aria-hidden="true"
        >
          <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </button>

    <!-- Body -->
    <Transition
      name="accordion"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
    >
      <div
        v-show="isOpen || isSolo"
        class="accordion-collapse"
        :id="`accordion-body-${group.id}`"
        role="region"
        :aria-label="`${group.name} benefits`"
      >
        <div class="accordion-body" :class="{ 'solo': isSolo }">
          <div class="accordion-body__grid">
            <BenefitCard
              v-for="(benefit, index) in group.benefits"
              :key="benefit.id || index"
              :benefit="benefit"
              :isLocked="isLocked"
              @select="$emit('benefit-select', $event)"
            />
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script>
import BenefitCard from '@/components/member/BenefitCard.vue';

export default {
  name: 'BenefitAccordion',
  components: { BenefitCard },
  emits: ['toggle', 'benefit-select'],
  props: {
    group: {
      type: Object,
      required: true,
      // { id: string, name: string, benefits: Array }
      default: () => ({ id: '', name: '', benefits: [] })
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    isSolo: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onEnter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.overflow = 'hidden';
      // Force repaint
      void el.offsetHeight;
      el.style.transition = 'height 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease';
      el.style.height = `${el.scrollHeight}px`;
      el.style.opacity = '1';
    },
    onAfterEnter(el) {
      el.style.height = '';
      el.style.opacity = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    onLeave(el) {
      // Set current height before transitioning to 0
      el.style.height = `${el.scrollHeight}px`;
      el.style.opacity = '1';
      el.style.overflow = 'hidden';
      // Force repaint
      void el.offsetHeight;
      el.style.transition = 'height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease';
      el.style.height = '0';
      el.style.opacity = '0';
    }
  }
}
</script>

<style scoped>
/* ===================== ACCORDION WRAPPER ===================== */
.benefit-accordion {
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  transition: box-shadow 0.2s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.benefit-accordion:not(.solo):hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.benefit-accordion.solo {
  border: none;
  border-radius: 0;
  background: transparent;
}

/* ===================== HEADER ===================== */
.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  padding: 16px 20px;
  background: white;
  border: none;
  cursor: pointer;
  gap: 12px;
  text-align: left;
  transition: background 0.15s ease;
  box-sizing: border-box;
  min-width: 0;
}

.accordion-header:hover {
  background: #FAFAFA;
}

.accordion-header:focus-visible {
  outline: 2px solid var(--primary-color, #F93E3E);
  outline-offset: -2px;
}

.accordion-header.open {
  border-bottom: 1px solid #F3F4F6;
}

/* Header left */
.accordion-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.accordion-header__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}

/* Header right */
.accordion-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.accordion-header__count {
  font-size: 12px;
  color: #9CA3AF;
  white-space: nowrap;
}

.accordion-header__locked-badge {
  font-size: 10px;
  font-weight: 600;
  color: #9CA3AF;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 2px 6px;
  letter-spacing: 0.3px;
}

.accordion-header__chevron {
  color: #9CA3AF;
  transition: transform 0.25s ease, color 0.15s ease;
  flex-shrink: 0;
}

.accordion-header__chevron.open {
  transform: rotate(180deg);
  color: var(--primary-color, #F93E3E);
}

/* ===================== COLLAPSE / BODY ===================== */
.accordion-collapse {
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.accordion-body {
  padding: 16px 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
}

.accordion-body.solo {
  padding: 0;
}

.accordion-body__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* Desktop: 3-column grid */
@media (min-width: 992px) {
  .accordion-body__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
}
</style>
